/**
 * Sistem de evenimente delegate + custom events (swipe) — port 1:1 din legacy.
 *
 * Echivalent cu lines 65-377 din public/js/common.js:
 *   - customEvents.swipe.init / .destroy
 *   - Node.prototype.on / off
 *   - NodeList.prototype.on / off
 *   - Node.prototype.trigger
 *   - window.on / off
 *
 * Storage: foloseste Symbol-uri unice per module-load (nu mai e nevoie de UID
 * via Date.now() ca-n legacy, pentru ca ES module e singleton pe pagina).
 */

import { isset } from './util.js';
import { device } from './device.js';
import './style.js'; // Node.prototype.data, offset (necesare pentru swipe + storage events)

// Symbol-uri unice pentru a stoca event registry + data pe noduri DOM.
// Nu folosim Date.now() ca-n legacy fiindca modulele se incarca o singura data
// per pagina si Symbol garanteaza unique key fara coliziuni cu alte libs.
export const EVENTS_KEY = Symbol('NavEvents');

type EventTuple = [namespace: string, selector: string | null, handler: EventListenerOrEventListenerObject];

interface NodeWithEvents extends Node {
    [EVENTS_KEY]?: Record<string, EventTuple[]>;
}

interface SwipeData {
    startEvnt: { position: { x: number; y: number }; offset: { x: number; y: number }; time: number; target: EventTarget | null };
    endEvnt:   { position: { x: number; y: number }; offset: { x: number; y: number }; time: number; target: EventTarget | null };
    direction: string;
    xAmount:   number;
    yAmount:   number;
    duration:  number;
}

interface CustomEventHandler {
    init:    (this: Node, ename: string, target: unknown, listener: unknown, opts: AddEventListenerOptions) => void;
    destroy: (this: Node, ename: string, target?: unknown) => void;
}

const customEvents: Record<string, CustomEventHandler> = {
    swipe: {
        init(this: Node, _ename, target, _listener, opts) {
            // Resort.config & device — accesate via window pentru a evita circular imports.
            const ResortRef = (window as unknown as { Resort?: { config?: { desktopSwipe?: boolean } } }).Resort;
            if (!ResortRef?.config?.desktopSwipe && !device.isMobile) return;

            const dataSelf = this as unknown as { data: (key: string, val?: number) => number | Node };
            const duplicate = ((dataSelf.data('swipe') as number) || 0) + 1;
            dataSelf.data('swipe', duplicate);
            if (duplicate > 1) return;

            const currentTarget = this as Node & { offset: () => { top: number; left: number }; trigger: (evt: string, sel?: unknown, data?: unknown) => Node; on: (events: string, sel: unknown, handler: unknown, opts?: AddEventListenerOptions) => void };

            let started = false;
            let hasSwiped = false;
            const originalCoord = { x: 0, y: 0 };
            const finalCoord = { x: 0, y: 0 };
            let startEvnt: SwipeData['startEvnt'];

            function touchStart(this: Node, e: PointerEvent | TouchEvent | MouseEvent) {
                const te = e as TouchEvent;
                const me = e as MouseEvent;
                originalCoord.x = te.targetTouches ? te.targetTouches[0]!.pageX : me.pageX;
                originalCoord.y = te.targetTouches ? te.targetTouches[0]!.pageY : me.pageY;
                finalCoord.x = originalCoord.x;
                finalCoord.y = originalCoord.y;
                started = true;

                const offset = currentTarget.offset();
                startEvnt = {
                    position: {
                        x: device.touchCapable ? te.touches[0]!.pageX : me.pageX,
                        y: device.touchCapable ? te.touches[0]!.pageY : me.pageY,
                    },
                    offset: {
                        x: device.touchCapable ? Math.round(te.changedTouches[0]!.pageX - offset.left) : Math.round(me.pageX - offset.left),
                        y: device.touchCapable ? Math.round(te.changedTouches[0]!.pageY - offset.top)  : Math.round(me.pageY - offset.top),
                    },
                    time: Date.now(),
                    target: e.target,
                };
            }

            function touchMove(this: Node, e: PointerEvent | TouchEvent | MouseEvent) {
                const te = e as TouchEvent;
                const me = e as MouseEvent;
                finalCoord.x = te.targetTouches ? te.targetTouches[0]!.pageX : me.pageX;
                finalCoord.y = te.targetTouches ? te.targetTouches[0]!.pageY : me.pageY;

                let swipedir: string | undefined;

                const parentData = (currentTarget.parentNode as unknown as { data?: (k: string) => unknown } | null);
                const selfData = currentTarget as unknown as { data: (k: string) => unknown };
                const ele_x_threshold = (parentData?.data?.('xthreshold')) ?? selfData.data('xthreshold');
                const ele_y_threshold = (parentData?.data?.('ythreshold')) ?? selfData.data('ythreshold');
                const h_threshold = (typeof ele_x_threshold !== 'undefined' && ele_x_threshold !== false && parseInt(String(ele_x_threshold))) ? parseInt(String(ele_x_threshold)) : device.swipe_h_threshold;
                const v_threshold = (typeof ele_y_threshold !== 'undefined' && ele_y_threshold !== false && parseInt(String(ele_y_threshold))) ? parseInt(String(ele_y_threshold)) : device.swipe_v_threshold;

                if (originalCoord.y > finalCoord.y && (originalCoord.y - finalCoord.y > v_threshold)) swipedir = 'swipeup';
                if (originalCoord.x < finalCoord.x && (finalCoord.x - originalCoord.x > h_threshold)) swipedir = 'swiperight';
                if (originalCoord.y < finalCoord.y && (finalCoord.y - originalCoord.y > v_threshold)) swipedir = 'swipedown';
                if (originalCoord.x > finalCoord.x && (originalCoord.x - finalCoord.x > h_threshold)) swipedir = 'swipeleft';

                if (swipedir !== undefined && started) {
                    originalCoord.x = 0; originalCoord.y = 0;
                    finalCoord.x = 0; finalCoord.y = 0;
                    started = false;

                    const offset = currentTarget.offset();
                    const endEvnt = {
                        position: {
                            x: device.touchCapable ? te.touches[0]!.pageX : me.pageX,
                            y: device.touchCapable ? te.touches[0]!.pageY : me.pageY,
                        },
                        offset: {
                            x: device.touchCapable ? Math.round(te.changedTouches[0]!.pageX - offset.left) : Math.round(me.pageX - offset.left),
                            y: device.touchCapable ? Math.round(te.changedTouches[0]!.pageY - offset.top)  : Math.round(me.pageY - offset.top),
                        },
                        time: Date.now(),
                        target: e.target,
                    };

                    const xAmount = Math.abs(startEvnt.position.x - endEvnt.position.x);
                    const yAmount = Math.abs(startEvnt.position.y - endEvnt.position.y);

                    const touchData: SwipeData = {
                        startEvnt, endEvnt,
                        direction: swipedir.replace('swipe', ''),
                        xAmount, yAmount,
                        duration: endEvnt.time - startEvnt.time,
                    };
                    hasSwiped = true;

                    currentTarget.trigger('swipe', null, touchData).trigger?.(swipedir, null, touchData);
                }
            }

            function touchEnd(this: Node, _e: Event) {
                started = false;
                hasSwiped = false;
            }

            if (target) {
                currentTarget.on(`${device.startevent}.swipe`, touchStart, target, { passive: true });
                currentTarget.on(`${device.moveevent}.swipe`,  touchMove,  target, { passive: true });
                currentTarget.on(`${device.endevent}.swipe`,   touchEnd,   target, { passive: true });
            } else {
                currentTarget.on(`${device.startevent}.swipe`, touchStart, null, { passive: true });
                currentTarget.on(`${device.moveevent}.swipe`,  touchMove,  null, { passive: true });
                currentTarget.on(`${device.endevent}.swipe`,   touchEnd,   null, { passive: true });
            }

            void hasSwiped; // marca folosirea (matches legacy variable scope)
            void opts;
        },

        destroy(this: Node, _ename, target) {
            const ResortRef = (window as unknown as { Resort?: { config?: { desktopSwipe?: boolean } } }).Resort;
            if (!ResortRef?.config?.desktopSwipe && !device.isMobile) return;

            const dataSelf = this as unknown as { data: (key: string, val?: number) => number | Node; off: (e: string, sel?: unknown) => void };
            const duplicate = ((dataSelf.data('swipe') as number) || 0) - 1;
            dataSelf.data('swipe', duplicate);

            if (!duplicate) {
                if (target) {
                    dataSelf.off(`${device.startevent}.swipe`, target);
                    dataSelf.off(`${device.moveevent}.swipe`,  target);
                    dataSelf.off(`${device.endevent}.swipe`,   target);
                } else {
                    dataSelf.off(`${device.startevent}.swipe`);
                    dataSelf.off(`${device.moveevent}.swipe`);
                    dataSelf.off(`${device.endevent}.swipe`);
                }
            }
        },
    },
};

// Alias-uri: swipeup/swiperight/swipedown/swipeleft folosesc aceeasi init/destroy.
customEvents['swipeup']    = customEvents['swipe']!;
customEvents['swiperight'] = customEvents['swipe']!;
customEvents['swipedown']  = customEvents['swipe']!;
customEvents['swipeleft']  = customEvents['swipe']!;

/* ---------- Node.prototype.on / off ---------- */

Node.prototype.on = function (this: Node, events: string, selectorOrHandler: unknown, handlerOrTarget?: unknown, opts?: AddEventListenerOptions) {
    const node = this as NodeWithEvents;
    const mergedOpts: AddEventListenerOptions = { ...(opts ?? {}) };
    if (!node[EVENTS_KEY]) node[EVENTS_KEY] = {};

    // Forma 1: on(events, handler, opts?) — fara selector delegate
    if (!isset(handlerOrTarget) || handlerOrTarget === null) {
        const handler = selectorOrHandler as EventListenerOrEventListenerObject;
        events.split(' ').forEach((evt) => {
            const [eName, namespace = '*'] = evt.split('.') as [string, string | undefined];
            const registry = node[EVENTS_KEY]!;
            if (!registry[eName]) registry[eName] = [];
            registry[eName]!.push([namespace, null, handler]);

            const custom = customEvents[eName];
            if (custom) custom.init.call(node, eName, null, handler, mergedOpts);

            node.addEventListener(eName, handler, mergedOpts);
        });
        return node as unknown as Node;
    }

    // Forma 2: on(events, selector, handler, opts?) — event delegation
    const selector = selectorOrHandler as string;
    const handler = handlerOrTarget as (this: Element, ev: Event) => void;
    events.split(' ').forEach((evt) => {
        const [eName, namespace = '*'] = evt.split('.') as [string, string | undefined];
        const registry = node[EVENTS_KEY]!;
        if (!registry[eName]) registry[eName] = [];
        registry[eName]!.push([namespace, selector, handler as unknown as EventListenerOrEventListenerObject]);

        const custom = customEvents[eName];
        if (custom) custom.init.call(node, eName, handler, selector, mergedOpts);

        node.addEventListener(eName, function (event: Event) {
            let target = event.target as Node | null;
            while (target && target !== node) {
                if ((target as Element).matches?.(selector)) {
                    handler.call(target as Element, event);
                }
                target = target.parentNode;
            }
        }, mergedOpts);
    });
    return node as unknown as Node;
} as Node['on'];

Node.prototype.off = function (this: Node, events: string, selector?: string) {
    const node = this as NodeWithEvents;
    if (!node[EVENTS_KEY]) return node as unknown as Node;

    events.split(' ').forEach((evt) => {
        const [eName, namespace = '*'] = evt.split('.') as [string, string | undefined];
        const list = node[EVENTS_KEY]![eName];
        if (!list) return;

        list.forEach((et, i) => {
            if (et[0] === namespace && (!selector || et[1] === selector)) {
                const custom = customEvents[eName];
                if (custom) custom.destroy.call(node, eName, et[1]);
                node.removeEventListener(eName, et[2]);
                list.splice(i, 1);
            }
        });
    });
    return node as unknown as Node;
} as Node['off'];

/* ---------- NodeList.prototype.on / off ---------- */

NodeList.prototype.on = function (this: NodeList, ...args: unknown[]) {
    this.forEach((node) => {
        ((node as Node).on as unknown as (...a: unknown[]) => unknown)(...args);
    });
    return this as unknown as NodeList;
} as NodeList['on'];

NodeList.prototype.off = function (this: NodeList, events: string, selector?: string) {
    this.forEach((node) => {
        (node as Node).off(events, selector);
    });
    return this as unknown as NodeList;
} as NodeList['off'];

/* ---------- window.on / off (shortcut) ---------- */

(window as unknown as { on: Node['on']; off: Node['off'] }).on  = Node.prototype.on;
(window as unknown as { on: Node['on']; off: Node['off'] }).off = Node.prototype.off;

/* ---------- Node.prototype.trigger ---------- */

Node.prototype.trigger = function (this: Node, ename: string, selector?: unknown, data?: unknown) {
    const node = this as NodeWithEvents;
    if (!node[EVENTS_KEY]) return node as unknown as Node;

    const [evtName, namespace = '*'] = ename.split('.') as [string, string | undefined];
    const handlers = node[EVENTS_KEY]![evtName];
    if (!handlers) return node as unknown as Node;

    const sel = selector ?? null;
    const pointerEvent = new PointerEvent(evtName) as PointerEvent & { data?: unknown };
    if (isset(data)) pointerEvent.data = data;

    handlers.forEach((handler) => {
        if (handler[0] === namespace && handler[1] === sel) {
            (handler[2] as unknown as (e: Event) => void).call(node, pointerEvent);
        }
    });
    return node as unknown as Node;
} as Node['trigger'];
